<template>
    <div :class="classes">
        <swiper
            ref="swiper"
            :options="swiperOptions"
            @ready="onReady"
            @tap="onTap"
            @resize="onResize"
            @slide-change="onSlideChange"
        >
            <!-- @slot default slot for DkSlider component -->
            <slot></slot>
        </swiper>

        <!-- Optional controls -->
        <div class="dk-slider__controls">
            <div slot="pagination" :class="`swiper-pagination pagination-${_uid}`"></div>
            <slot name="button-prev" v-bind="{ _uid }">
                <div :class="`swiper-button-prev prev-${_uid} u-hidden-till--medium`">
                    <button class="btn btn--tertiary btn--round">
                        <icon name="generic-arrow-left" />
                    </button>
                </div>
            </slot>
            <slot name="button-next" v-bind="{ _uid }">
                <div :class="`swiper-button-next next-${_uid} u-hidden-till--medium`">
                    <button class="btn btn--tertiary btn--round">
                        <icon name="generic-arrow-right" />
                    </button>
                </div>
            </slot>
            <slot name="scrollbar" v-bind="{ _uid }">
                <div :class="`swiper-scrollbar scrollbar-${_uid}`"></div>
            </slot>
        </div>
    </div>
</template>

<script>
import merge from 'lodash.merge';
import debounce from 'lodash/debounce';
import { isMobile } from '@v4/common/utils/media-query';
import { swiper, swiperSlide } from 'vue-awesome-swiper';

/**
 * A slider component which is wrapping Swiper Slider.
 *
 * @version 4.0.0
 * @see [Vue-Awesome-Swiper](https://github.com/surmon-china/vue-awesome-swiper)
 * @see [Swiper API](https://github.com/nolimits4web/Swiper/blob/Swiper4/API.md)
 * @example ./../examples/DkSlider.md
 */
export default {
    name: 'DkSlider',
    components: {
        swiper,
        // eslint-disable-next-line vue/no-unused-components
        swiperSlide,
        Icon: () => ({
            component: import(/* webpackChunkName: "Icon-vue" */ '@v4/common/components/atoms/Icon.vue'),
        }),
    },
    props: {
        /**
         * Theme name.
         */
        theme: {
            type: String,
            default: () => 'default',
        },
        /**
         * Custom options.
         */
        options: {
            type: Object,
            default: () => ({}),
        },
        /**
         * @see [Swiper Parameters](http://idangero.us/swiper/api/#parameters).
         */
        swiperOpts: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            classes: ['dk-slider', `dk-slider--${this.theme}`, this.options.clickable ? 'dk-slider--clickable' : ''],
        };
    },
    computed: {
        swiper() {
            return this.$refs.swiper.swiper;
        },
        opts() {
            return merge(
                {
                    pagination: true,
                    navigation: true,
                    scrollbar: false,
                },
                this.options
            );
        },
        swiperOptions() {
            return merge(
                {
                    slidesPerView: 3,
                    pagination: {
                        el: this.opts.pagination ? `.swiper-pagination.pagination-${this._uid}` : null,
                        clickable: true,
                    },
                    navigation: {
                        nextEl: this.opts.navigation ? `.swiper-button-next.next-${this._uid}` : null,
                        prevEl: this.opts.navigation ? `.swiper-button-prev.prev-${this._uid}` : null,
                    },
                    scrollbar: {
                        el: this.opts.scrollbar ? `.swiper-scrollbar.scrollbar-${this._uid}` : null,
                    },
                    breakpoints: {
                        767: {
                            slidesPerView: 1,
                        },
                    },
                },
                this.swiperOpts
            );
        },
    },
    methods: {
        /**
         * Allows the slides to slide. Dipending on device and on slide numbers.
         *
         * @return {void}
         */
        allowSlide() {
            const slides = this.swiper.slides.length;

            if (!slides) {
                return;
            }

            if (
                (!isMobile() && (slides === 1 || slides <= this.swiperOptions.slidesPerView)) ||
                (isMobile() && slides === 1)
            ) {
                this.swiper.allowSlidePrev = false;
                this.swiper.allowSlideNext = false;
            }
        },
        onReady() {
            /**
             * Fired when swiper slider is ready.
             *
             * @event ready
             * @type {object} Swiper instance
             */
            this.$emit('ready', this.swiper);
            const slides = this.swiper.slides.length;

            if (slides === 1) {
                this.classes.push('has-one');
            } else if (slides <= this.swiperOptions.slidesPerView) {
                this.classes.push('has-less-then-default');
            }

            this.allowSlide();
        },
        /**
         * Using debounce in order to avoid emitting events multipe times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onTap: debounce(function($event) {
            let { clickedIndex } = this.swiper;
            const { clickedSlide } = this.swiper;

            if (clickedSlide) {
                const slideIndex = clickedSlide.getAttribute('data-swiper-slide-index');

                if (this.swiperOptions.loop && slideIndex) {
                    clickedIndex = parseInt(slideIndex, 10);
                }

                /**
                 * Fired when a specific slide is clicked.
                 *
                 * @event clickedSlide
                 * @type {object}
                 * @property {number} clickedIndex Slide index
                 * @property {object} clickedSlide Slide HTML element
                 * @property {object} $event PointerEvent
                 */
                this.$emit('clicked-slide', { clickedIndex, clickedSlide, $event });
            }
        }),
        /**
         * Using debounce in order to avoid emitting events multipe times.
         *
         * @see {@link https://github.com/surmon-china/vue-awesome-swiper/issues/286| Click event emit twice}
         * @return {void}
         */
        onResize: debounce(function() {
            this.allowSlide();
        }),
        /**
         * Triggered on slide change
         *
         * @return {void}
         */
        onSlideChange() {
            this.$emit('slide-change');
        },
    },
};
</script>

<style lang="scss" src="@v4/common/components/molecules/DkSlider/_index.scss"></style>
